import { useSyncExternalStore } from "react";
import { novelLinkKey, shortCodeFromLinkKey } from "@lorevok/domain";

// Short novel links (/novel/<slug>-<code>, /n/<code>) on the web side.
//
// App state keeps REAL novel ids — every API call (views, likes, comments,
// edits) is by id. This module maps between the two: `apiClient` records each
// novel's link key whenever a novel or a list of summaries arrives, the URL
// sync writes `novelLinkSegment(id)` into the address bar, and a deep link's
// key is turned back into an id with `resolveNovelLink` before the page opens.
// A novel whose key is not known yet keeps its id in the URL — old id links
// work forever — and the address is upgraded in place once the key arrives
// (`useNovelLinksVersion` re-runs the sync).

const keyById = new Map<string, string>();
const idByCode = new Map<string, string>();
const listeners = new Set<() => void>();
let version = 0;

/** Records a novel's link key (no-op for a novel storage has not given a code). */
export function rememberNovelLink(novel: { id: string; title: string; short_code?: string }): void {
  if (!novel.short_code) return;
  const key = novelLinkKey(novel.title, novel.short_code);
  idByCode.set(novel.short_code, novel.id);
  if (keyById.get(novel.id) === key) return;
  keyById.set(novel.id, key);
  version += 1;
  for (const listener of listeners) listener();
}

export function rememberNovelLinks(novels: ReadonlyArray<{ id: string; title: string; short_code?: string }>): void {
  for (const novel of novels) rememberNovelLink(novel);
}

/** The URL segment for a novel: its link key when known, else its id. */
export function novelLinkSegment(id: string): string {
  return keyById.get(id) ?? id;
}

/** A counter that changes whenever a new link key is recorded. */
export function useNovelLinksVersion(): number {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => version,
    () => 0
  );
}

/** True when a URL segment is a link key that still needs resolving to an id. */
export function needsLinkResolution(segment: string): boolean {
  const code = shortCodeFromLinkKey(segment);
  return code !== null && !idByCode.has(code);
}

/** The id a URL segment names, when that is known without a request. */
export function knownNovelId(segment: string): string {
  const code = shortCodeFromLinkKey(segment);
  return (code && idByCode.get(code)) || segment;
}

/**
 * The real id behind a URL segment. A link key is read through the API (which
 * accepts keys on `GET /api/novels/:key`); an unreadable key comes back as is,
 * so the page shows its usual not-found state.
 */
export async function resolveNovelLink(
  segment: string,
  load: (key: string) => Promise<{ novel: { id: string }; source: string }>
): Promise<string> {
  if (!needsLinkResolution(segment)) return knownNovelId(segment);
  try {
    const result = await load(segment);
    return result.source === "fallback" ? segment : result.novel.id;
  } catch {
    return segment;
  }
}

/** Test hook: forget every recorded key. */
export function resetNovelLinks(): void {
  keyById.clear();
  idByCode.clear();
  version += 1;
  for (const listener of listeners) listener();
}
